
import { useCallback, useEffect } from 'react';
import { useReactFlow, type Node, type Edge } from '@xyflow/react';

interface UseFlowKeyboardConfig {
    /** Callback when delete is pressed on selected nodes */
    onDeleteNodes?: (nodes: Node[]) => void;
    /** Callback when delete is pressed on selected edges */
    onDeleteEdges?: (edges: Edge[]) => void;
    /** Whether keyboard shortcuts are enabled */
    enabled?: boolean;
}

export function useFlowKeyboard({
    onDeleteNodes,
    onDeleteEdges,
    enabled = true,
}: UseFlowKeyboardConfig) {
    const reactFlowInstance = useReactFlow();

    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!enabled) return;

            // Ignore if we're in an input field
            if (
                event.target instanceof HTMLInputElement ||
                event.target instanceof HTMLTextAreaElement
            ) {
                return;
            }

            // Handle Delete/Backspace
            if (event.key === 'Delete' || event.key === 'Backspace') {
                const selectedNodes = reactFlowInstance.getNodes().filter((n) => n.selected);
                const selectedEdges = reactFlowInstance.getEdges().filter((e) => e.selected);

                if (selectedNodes.length > 0 && onDeleteNodes) {
                    event.preventDefault();
                    onDeleteNodes(selectedNodes);
                }

                if (selectedEdges.length > 0 && onDeleteEdges) {
                    event.preventDefault();
                    onDeleteEdges(selectedEdges);
                }
            }

            // Handle Ctrl+A to select all
            if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
                event.preventDefault();
                const allNodes = reactFlowInstance.getNodes().map((n) => ({ ...n, selected: true }));
                const allEdges = reactFlowInstance.getEdges().map((e) => ({ ...e, selected: true }));
                reactFlowInstance.setNodes(allNodes);
                reactFlowInstance.setEdges(allEdges);
            }
        },
        [enabled, reactFlowInstance, onDeleteNodes, onDeleteEdges]
    );

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onKeyDown]);
}

